import { useCallback, useEffect, useReducer, useRef } from "react";
import { AutoClosePhase, autoCloseAnnouncement, autoCloseMessage, autoCloseReducer, CLOSE_CHECK_DELAY_MS, initialAutoCloseState } from "./autoCloseCountdown";

interface AutoCloseAfterSubmit {
  phase: AutoClosePhase;
  secondsLeft: number;
  /** The text on screen, seconds included. */
  message: string;
  /** The text for the live region: the start once, then only the outcome. */
  announcement: string;
  closeNow: () => void;
  stay: () => void;
}

/**
 * Counts down from the moment it mounts and then asks the browser to close the tab. Mount it only on the
 * result screen of a successful submission: starting on mount is what keeps it off the form itself, off a
 * failed submit and off the "already submitted" screen. Unmounting (the learner opens a fresh form with
 * "Submit another response") clears every timer.
 *
 * `enabled` false leaves it idle — no timers, no close.
 */
export const useAutoCloseAfterSubmit = (enabled: boolean): AutoCloseAfterSubmit => {
  const [state, dispatch] = useReducer(autoCloseReducer, initialAutoCloseState);
  // `window.close()` is asked for once per mount, whatever re-renders follow.
  const closeRequested = useRef(false);

  // One tick per second while counting. Re-armed on every second, so a Stay or Close now in between
  // simply leaves nothing to re-arm.
  useEffect(() => {
    if (!enabled || state.phase !== "counting") return undefined;
    const timer = window.setTimeout(() => dispatch({ type: "tick" }), 1000);
    return () => window.clearTimeout(timer);
  }, [enabled, state.phase, state.secondsLeft]);

  useEffect(() => {
    if (!enabled || state.phase !== "closing" || closeRequested.current) return undefined;
    closeRequested.current = true;
    window.close();
    // A browser that refuses (the tab was not opened by a link or a script) does so silently: no error,
    // the page simply stays. Still being here a moment later is the only sign of it.
    const timer = window.setTimeout(() => {
      if (!window.closed) dispatch({ type: "closeRefused" });
    }, CLOSE_CHECK_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [enabled, state.phase]);

  const closeNow = useCallback(() => dispatch({ type: "closeNow" }), []);
  const stay = useCallback(() => dispatch({ type: "stay" }), []);

  return { ...state, message: autoCloseMessage(state), announcement: autoCloseAnnouncement(state), closeNow, stay };
};

export default useAutoCloseAfterSubmit;
